import { useCallback, useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Dialog, DialogContent, DialogClose, DialogFooter, Button } from '@/components/ui'
import type { MenuItem, ComboGroup } from '@/types/menu'
import type { ComboSelections } from '@/types/cart'
import { useAppDispatch } from '@/store/hooks'
import { addItem } from '@/store/cartSlice'
import { isIngredientEnabled, getSelectionLimit } from '@/utils/ruleEngine'
import { formatPrice } from '@/utils/formatting'
import type { ModifierOption, ModifierOptionVariation } from '@/types/modifier'

import {
  useItemCustomization,
  useRuleEvaluation,
  usePriceCalculation,
  useCartValidation,
} from '@/hooks/customization'
import { CustomizationContent } from './CustomizationContent'
import { VariationPickerPage } from './VariationPickerPage'

interface ItemCustomizationModalProps {
  item: MenuItem | null
  isOpen: boolean
  onClose: () => void
}

export function ItemCustomizationModal({
  item,
  isOpen,
  onClose,
}: ItemCustomizationModalProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Page navigation state
  const [currentPage, setCurrentPage] = useState<'main' | 'variation'>('main')
  const [variationPageData, setVariationPageData] = useState<{
    groupId: string
    groupName: string
    option: ModifierOption
  } | null>(null)
  const [tempSelectedVariation, setTempSelectedVariation] = useState<ModifierOptionVariation | null>(null)

  // State management
  const {
    quantity, setQuantity,
    selectedModifiers, setSelectedModifiers,
    mainMealCustomizations, setMainMealCustomizations,
    comboGroupSelections, setComboGroupSelections,
    ingredientVariationSelections, setIngredientVariationSelections,
    specialInstructions, setSpecialInstructions,
    mainMealExpanded, setMainMealExpanded,
  } = useItemCustomization(item)

  // Rule evaluation
  const {
    singleItemRuleResult, mainMealRuleResult,
    getGroupOptionsToDisplay, getRowIngredientsToDisplay,
  } = useRuleEvaluation(item, selectedModifiers, mainMealCustomizations, setSelectedModifiers, setMainMealCustomizations)

  const { totalPrice } = usePriceCalculation(item, quantity, selectedModifiers, comboGroupSelections, mainMealCustomizations)
  const { canAddToCart, remainingRequired } = useCartValidation({
    item, selectedModifiers, comboGroupSelections, mainMealCustomizations, singleItemRuleResult,
  })

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setCurrentPage('main')
      setVariationPageData(null)
      setTempSelectedVariation(null)
      onClose()
    }
  }

  useEffect(() => {
    setCurrentPage('main')
    setVariationPageData(null)
    setTempSelectedVariation(null)
  }, [item])

  // Modifier toggle handler
  const handleModifierToggle = useCallback((
    groupId: string, groupName: string, optionId: string, optionName: string,
    price: number, variationId?: string, variationName?: string
  ) => {
    const group = item?.modifierGroups?.find((g) => g.id === groupId)
    const maxSelections = group
      ? getSelectionLimit(groupId, singleItemRuleResult, group.maxSelections || group.options.length)
      : undefined

    setSelectedModifiers((prev) => {
      if (maxSelections === 1) {
        return [...prev.filter((m) => m.groupId !== groupId),
          { groupId, groupName, optionId, optionName, priceAdjustment: price, variationId, variationName }]
      }
      const exists = prev.find((m) => m.groupId === groupId && m.optionId === optionId)
      if (exists && variationId) {
        return prev.map((m) => m.groupId === groupId && m.optionId === optionId
          ? { ...m, variationId, variationName, priceAdjustment: price } : m)
      }
      if (exists) return prev.filter((m) => !(m.groupId === groupId && m.optionId === optionId))
      const currentCount = prev.filter((m) => m.groupId === groupId).length
      if (maxSelections && currentCount >= maxSelections) return prev
      return [...prev, { groupId, groupName, optionId, optionName, priceAdjustment: price, variationId, variationName }]
    })
  }, [item?.modifierGroups, singleItemRuleResult, setSelectedModifiers])

  const handleOpenVariationPage = useCallback((groupId: string, groupName: string, option: ModifierOption) => {
    const currentSelection = selectedModifiers.find((m) => m.groupId === groupId && m.optionId === option.id)
    if (currentSelection?.variationId && option.variationGroup) {
      setTempSelectedVariation(option.variationGroup.variations.find((v) => v.id === currentSelection.variationId) || null)
    } else {
      setTempSelectedVariation(option.variationGroup?.variations.find((v) => v.is_default) || null)
    }
    setVariationPageData({ groupId, groupName, option })
    setCurrentPage('variation')
  }, [selectedModifiers])

  const handleConfirmVariation = () => {
    if (!variationPageData || !tempSelectedVariation) return
    const { groupId, groupName, option } = variationPageData
    const price = tempSelectedVariation.price !== undefined ? tempSelectedVariation.price : (option.priceAdjustment || 0)
    handleModifierToggle(groupId, groupName, option.id, option.name, price, tempSelectedVariation.id, tempSelectedVariation.name)
    setCurrentPage('main')
    setVariationPageData(null)
    setTempSelectedVariation(null)
  }

  const handleQuantityModifierChange = useCallback((
    groupId: string, groupName: string, optionId: string, optionName: string, price: number, newQty: number
  ) => {
    setSelectedModifiers((prev) => {
      const rest = prev.filter((m) => !(m.groupId === groupId && m.optionId === optionId))
      if (newQty <= 0) return rest
      return [...rest, { groupId, groupName, optionId, optionName, priceAdjustment: price, quantity: newQty }]
    })
  }, [setSelectedModifiers])

  const handleComboGroupToggle = (group: ComboGroup, optionId: string) => {
    const option = group.options.find((o) => o.id === optionId)
    if (!option) return
    setComboGroupSelections((prev) => {
      const isSelected = prev.some((s) => s.groupId === group.id && s.optionId === optionId)
      if (isSelected) return prev.filter((s) => !(s.groupId === group.id && s.optionId === optionId))
      if (prev.filter((s) => s.groupId === group.id).length >= group.maxSelections) return prev
      return [...prev, { groupId: group.id, groupName: group.name, optionId: option.id, optionName: option.ingredient.name, additionalPrice: option.additionalPrice }]
    })
  }

  const checkIngredientEnabled = useCallback(
    (groupId: string, optionId: string) =>
      isIngredientEnabled(groupId, optionId, singleItemRuleResult, true),
    [singleItemRuleResult]
  )

  const getLimit = useCallback(
    (groupId: string) => {
      const group = item?.modifierGroups?.find((g) => g.id === groupId)
      return getSelectionLimit(groupId, singleItemRuleResult, group?.maxSelections || group?.options.length || 999)
    },
    [item?.modifierGroups, singleItemRuleResult]
  )

  const handleAddToCart = () => {
    if (!item || !canAddToCart) return
    let menuItemForCart = item
    if (item.ingredientVariationGroup) {
      const selMod = selectedModifiers.find((m) => m.groupId === item.ingredientVariationGroup!.id)
      if (selMod) {
        const selVar = item.ingredientVariationGroup.variations.find((v) => v.id === selMod.optionId)
        if (selVar?.price !== undefined) menuItemForCart = { ...item, price: selVar.price }
      }
    }
    let comboSelectionsPayload: ComboSelections | undefined
    if (item.itemType === 'combo' && item.comboProduct) {
      comboSelectionsPayload = {
        mainMealCustomizations: mainMealCustomizations.length > 0 ? mainMealCustomizations : undefined,
        groupSelections: comboGroupSelections,
      }
    }
    dispatch(addItem({ menuItem: menuItemForCart, selectedModifiers, quantity, specialInstructions: specialInstructions || undefined, comboSelections: comboSelectionsPayload }))
    handleOpenChange(false)
  }

  if (!item) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl w-full max-h-[90vh] flex flex-col !p-0 gap-0 overflow-hidden" hideCloseButton>
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide overscroll-contain" ref={scrollContainerRef}>
          {/* Floating close */}
          <div className="sticky top-0 z-20 pointer-events-none h-0 overflow-visible">
            <div className="flex justify-end p-3">
              <DialogClose className="pointer-events-auto w-10 h-10 rounded-full bg-black/30 dark:bg-white/20 backdrop-blur-xl text-white shadow-lg flex items-center justify-center cursor-pointer hover:bg-black/50 dark:hover:bg-white/30 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </div>

          {currentPage === 'main' ? (
            <CustomizationContent
              item={item}
              quantity={quantity}
              onQuantityChange={setQuantity}
              selectedModifiers={selectedModifiers}
              setSelectedModifiers={setSelectedModifiers}
              specialInstructions={specialInstructions}
              onSpecialInstructionsChange={setSpecialInstructions}
              onModifierToggle={handleModifierToggle}
              onQuantityModifierChange={handleQuantityModifierChange}
              isIngredientEnabled={checkIngredientEnabled}
              onOpenVariationPicker={handleOpenVariationPage}
              singleItemRuleResult={singleItemRuleResult}
              getGroupOptionsToDisplay={getGroupOptionsToDisplay}
              getRowIngredientsToDisplay={getRowIngredientsToDisplay}
              mainMealCustomizations={mainMealCustomizations}
              setMainMealCustomizations={setMainMealCustomizations}
              comboGroupSelections={comboGroupSelections}
              onComboGroupToggle={handleComboGroupToggle}
              ingredientVariationSelections={ingredientVariationSelections}
              setIngredientVariationSelections={setIngredientVariationSelections}
              mainMealRuleResult={mainMealRuleResult}
              mainMealExpanded={mainMealExpanded}
              setMainMealExpanded={setMainMealExpanded}
              groupRefs={groupRefs}
              getSelectionLimit={getLimit}
            />
          ) : (
            variationPageData && (
              <VariationPickerPage
                option={variationPageData.option}
                groupName={variationPageData.groupName}
                selectedVariation={tempSelectedVariation}
                onSelect={setTempSelectedVariation}
                onBack={() => { setCurrentPage('main'); setVariationPageData(null); setTempSelectedVariation(null) }}
                currency={item.currency || 'CAD'}
              />
            )
          )}
        </div>

        <DialogFooter className="shrink-0 flex-col sm:flex-col gap-3 px-5 sm:px-6 py-4 bg-background border-t border-border shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
          {currentPage === 'main' ? (
            <Button onClick={handleAddToCart} disabled={!canAddToCart} size="lg" className="w-full text-base sm:text-lg h-12 sm:h-14 rounded-xl font-semibold" variant="black">
              {canAddToCart
                ? t('common:addToCartWithPrice', { price: formatPrice(totalPrice, item.currency, false) })
                : t('common:makeRequiredSelections', { count: remainingRequired, price: formatPrice(totalPrice, item.currency, false) })}
            </Button>
          ) : (
            <Button onClick={handleConfirmVariation} disabled={!tempSelectedVariation} size="lg" className="w-full text-base sm:text-lg h-12 sm:h-14 rounded-xl font-semibold" variant="black">
              {t('common:selectVariation')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
